import { t } from "@/lib/i18n/interpolate";
import { stripFinalPeriod } from "@/lib/tool-kit/publishing/authors";
import {
  paragraphToHtml,
  paragraphToPlain,
  type RichParagraph,
} from "@/lib/tool-kit/publishing/rich-text";

/**
 * The author block of a manuscript's title page: names with superscript affiliation
 * marks, the numbered affiliation list, and the notes for equal contribution and the
 * corresponding author.
 *
 * Conventions, from the publishers' own templates: affiliations are numbered in order of
 * first appearance as you read the author list (PLOS's LaTeX template numbers them;
 * Elsevier journals traditionally use lowercase letters), the corresponding author is
 * marked with an asterisk, and equal contribution with a symbol of its own — PLOS uses
 * ☯, many journals † or ‡.
 */

export type MarkerStyle = "numbers" | "letters";
export type EqualSymbolId = "dagger" | "doubleDagger" | "hash" | "yinyang";
export type FinalJoin = "comma" | "and" | "ampersand";

export const EQUAL_SYMBOLS: Readonly<
  Record<EqualSymbolId, { text: string; latex: string; latexPackage?: string }>
> = {
  // authblk expands its marks inside \xdef, so a command mark is protected.
  dagger: { text: "†", latex: "\\protect\\textdagger" },
  doubleDagger: { text: "‡", latex: "\\protect\\textdaggerdbl" },
  hash: { text: "#", latex: "\\#" },
  yinyang: {
    text: "☯",
    latex: "\\protect\\Yinyang",
    latexPackage: "marvosym",
  },
};

export const CORRESPONDING_MARK = "*";

export interface AuthorInput {
  name: string;
  affiliations: readonly string[];
  orcid: string;
  email: string;
  corresponding: boolean;
  equal: boolean;
}

export interface BlockOptions {
  markers: MarkerStyle;
  equalSymbol: EqualSymbolId;
}

export interface NumberedAffiliation {
  marker: string;
  text: string;
}

export interface BlockAuthor {
  name: string;
  /** Affiliation marks, in ascending order. */
  marks: string[];
  /** Equal-contribution and corresponding marks, in that order. */
  symbols: string[];
  /** A checksum-valid ORCID iD, hyphenated. */
  orcid: string | null;
  email: string;
  corresponding: boolean;
  equal: boolean;
}

export interface AuthorBlock {
  authors: BlockAuthor[];
  affiliations: NumberedAffiliation[];
}

/* ---------------------------------------------------------------- markers */

/** 0 → "1" or "a"; letters run a…z, aa, ab… */
export function affiliationMarker(index: number, style: MarkerStyle): string {
  if (style === "numbers") {
    return String(index + 1);
  }
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const digit = (n - 1) % 26;
    out = String.fromCharCode(97 + digit) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

const RE_SPACES = /\s+/g;
const RE_TRAILING_SEPARATORS = /[\s,;]+$/;

/**
 * Whitespace collapsed and a trailing comma, semicolon or sentence full stop removed —
 * the form printed. The full stop of an abbreviation stays: "Boston, MA, U.S.A."
 */
export function normalizeAffiliation(text: string): string {
  return stripFinalPeriod(
    text.replace(RE_SPACES, " ").trim().replace(RE_TRAILING_SEPARATORS, "")
  );
}

/** Two spellings that differ only in case or spacing are one affiliation. */
function affiliationKey(text: string): string {
  return normalizeAffiliation(text).toLowerCase();
}

/* ------------------------------------------------------------------- ORCID */

const RE_ORCID_PREFIX = /^(?:https?:\/\/)?(?:www\.)?orcid\.org\//i;
const RE_ORCID = /^(\d{4})-?(\d{4})-?(\d{4})-?(\d{3}[\dX])$/i;

/**
 * The ORCID check character: ISO/IEC 7064:2003 MOD 11-2 over the first fifteen digits,
 * as ORCID documents it — "X" stands for 10.
 */
export function orcidCheckCharacter(base15: string): string {
  let total = 0;
  for (const char of base15) {
    total = (total + Number(char)) * 2;
  }
  const result = (12 - (total % 11)) % 11;
  return result === 10 ? "X" : String(result);
}

export type OrcidStatus = "empty" | "valid" | "checksum" | "format";

/** Read "0000-0002-1825-0097", "https://orcid.org/…" or sixteen bare characters. */
export function parseOrcid(raw: string): { status: OrcidStatus; id: string } {
  const trimmed = raw.trim().replace(RE_ORCID_PREFIX, "");
  if (trimmed === "") {
    return { status: "empty", id: "" };
  }
  const match = RE_ORCID.exec(trimmed);
  if (!match) {
    return { status: "format", id: trimmed };
  }
  const id = `${match[1]}-${match[2]}-${match[3]}-${match[4].toUpperCase()}`;
  const digits = id.replaceAll("-", "");
  const valid = orcidCheckCharacter(digits.slice(0, 15)) === digits.at(-1);
  return { status: valid ? "valid" : "checksum", id };
}

export function orcidUrl(id: string): string {
  return `https://orcid.org/${id}`;
}

/* -------------------------------------------------------------- the block */

export function buildAuthorBlock(
  authors: readonly AuthorInput[],
  options: BlockOptions
): AuthorBlock {
  const indexByKey = new Map<string, number>();
  const affiliations: NumberedAffiliation[] = [];
  const blockAuthors: BlockAuthor[] = [];
  for (const author of authors) {
    const name = author.name.replace(RE_SPACES, " ").trim();
    if (name === "") {
      continue;
    }
    const indices = new Set<number>();
    for (const raw of author.affiliations) {
      const text = normalizeAffiliation(raw);
      if (text === "") {
        continue;
      }
      const key = affiliationKey(text);
      let index = indexByKey.get(key);
      if (index === undefined) {
        index = affiliations.length;
        indexByKey.set(key, index);
        affiliations.push({
          marker: affiliationMarker(index, options.markers),
          text,
        });
      }
      indices.add(index);
    }
    const symbols: string[] = [];
    if (author.equal) {
      symbols.push(EQUAL_SYMBOLS[options.equalSymbol].text);
    }
    if (author.corresponding) {
      symbols.push(CORRESPONDING_MARK);
    }
    const orcid = parseOrcid(author.orcid);
    blockAuthors.push({
      name,
      marks: [...indices]
        .sort((a, b) => a - b)
        .map((index) => affiliationMarker(index, options.markers)),
      symbols,
      orcid: orcid.status === "valid" ? orcid.id : null,
      email: author.email.trim(),
      corresponding: author.corresponding,
      equal: author.equal,
    });
  }
  return { authors: blockAuthors, affiliations };
}

/** "1,2†*" — affiliation marks joined by commas, symbols appended (PLOS's form). */
export function superscriptFor(author: BlockAuthor): string {
  return `${author.marks.join(",")}${author.symbols.join("")}`;
}

/* ----------------------------------------------------------------- output */

export interface BlockTemplates {
  /** Between names: ", " */
  nameSeparator: string;
  /** Before the last name when joining with a word: " and " */
  finalAnd: string;
  /** Before the last name with an ampersand: " & " */
  finalAmpersand: string;
  /** "These authors contributed equally to this work." */
  equalNote: string;
  /** "{label}: {emails}", the label already pluralized by the widget. */
  correspondingNote: string;
  correspondingLabel: string;
  placeholderEmail: string;
  orcidHeading: string;
  /** "{name}: {url}" */
  orcidLine: string;
  emailSeparator: string;
}

export interface BlockSections {
  authorLine: RichParagraph;
  affiliations: RichParagraph[];
  notes: RichParagraph[];
  orcids: RichParagraph[];
}

function separatorBefore(
  index: number,
  count: number,
  join: FinalJoin,
  templates: BlockTemplates
): string {
  if (index === 0) {
    return "";
  }
  if (index === count - 1 && join === "and") {
    return count === 2
      ? templates.finalAnd
      : `${templates.nameSeparator.trimEnd()}${templates.finalAnd}`;
  }
  if (index === count - 1 && join === "ampersand") {
    return templates.finalAmpersand;
  }
  return templates.nameSeparator;
}

function correspondingEmails(
  block: AuthorBlock,
  templates: BlockTemplates
): string {
  return block.authors
    .filter((author) => author.corresponding)
    .map(
      (author) =>
        author.email || t(templates.placeholderEmail, { name: author.name })
    )
    .join(templates.emailSeparator);
}

export function blockSections(
  block: AuthorBlock,
  options: BlockOptions & { join: FinalJoin },
  templates: BlockTemplates
): BlockSections {
  const count = block.authors.length;
  const authorLine: RichParagraph = block.authors.flatMap((author, index) => {
    const runs: RichParagraph = [
      {
        text: `${separatorBefore(index, count, options.join, templates)}${author.name}`,
      },
    ];
    const sup = superscriptFor(author);
    if (sup !== "") {
      runs.push({ text: sup, sup: true });
    }
    return runs;
  });
  const affiliations = block.affiliations.map(
    (affiliation): RichParagraph => [
      { text: affiliation.marker, sup: true },
      { text: ` ${affiliation.text}` },
    ]
  );
  const notes: RichParagraph[] = [];
  if (block.authors.some((author) => author.equal)) {
    notes.push([
      {
        text: `${EQUAL_SYMBOLS[options.equalSymbol].text} ${templates.equalNote}`,
      },
    ]);
  }
  if (block.authors.some((author) => author.corresponding)) {
    notes.push([
      {
        text: `${CORRESPONDING_MARK} ${t(templates.correspondingNote, {
          label: templates.correspondingLabel,
          emails: correspondingEmails(block, templates),
        })}`,
      },
    ]);
  }
  const withOrcid = block.authors.filter((author) => author.orcid);
  const orcids: RichParagraph[] =
    withOrcid.length === 0
      ? []
      : [
          [{ text: templates.orcidHeading, bold: true }],
          ...withOrcid.map(
            (author): RichParagraph => [
              {
                text: t(templates.orcidLine, {
                  name: author.name,
                  url: orcidUrl(author.orcid ?? ""),
                }),
              },
            ]
          ),
        ];
  return { authorLine, affiliations, notes, orcids };
}

function nonEmptySections(sections: BlockSections): RichParagraph[][] {
  return [
    [sections.authorLine],
    sections.affiliations,
    sections.notes,
    sections.orcids,
  ].filter((group) => group.length > 0 && group[0].length > 0);
}

/** Plain text with Unicode superscripts: lines within a section, a blank line between. */
export function blockToPlain(sections: BlockSections): string {
  return nonEmptySections(sections)
    .map((group) =>
      group
        .map((paragraph) =>
          paragraphToPlain(paragraph, { unicodeSuperscript: true })
        )
        .join("\n")
    )
    .join("\n\n");
}

/** HTML: one `<p>` per line, superscripts as `<sup>`. Also what rich copy pastes. */
export function blockToHtml(sections: BlockSections): string {
  return nonEmptySections(sections)
    .map((group) =>
      group
        .map((paragraph) => `<p>${paragraphToHtml(paragraph)}</p>`)
        .join("\n")
    )
    .join("\n");
}

/* ------------------------------------------------------------------ LaTeX */

const LATEX_SPECIALS: Record<string, string> = {
  "\\": "\\textbackslash{}",
  "&": "\\&",
  "%": "\\%",
  $: "\\$",
  "#": "\\#",
  _: "\\_",
  "{": "\\{",
  "}": "\\}",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
};
const RE_LATEX_SPECIAL = /[\\&%$#_{}~^]/g;

export function escapeLatex(text: string): string {
  return text.replace(RE_LATEX_SPECIAL, (char) => LATEX_SPECIALS[char] ?? char);
}

export interface LatexTemplates {
  preambleComment: string;
  bodyComment: string;
  equalNote: string;
  correspondingNote: string;
  correspondingLabel: string;
  placeholderEmail: string;
  emailSeparator: string;
}

/** The block for the `authblk` package: `\author[marks]{name}` and `\affil[mark]{text}`. */
export function blockToLatex(
  block: AuthorBlock,
  options: BlockOptions,
  templates: LatexTemplates
): string {
  const equal = EQUAL_SYMBOLS[options.equalSymbol];
  const packages = ["\\usepackage{authblk}"];
  if (block.authors.some((author) => author.orcid)) {
    packages.push("\\usepackage{orcidlink}");
  }
  if (equal.latexPackage && block.authors.some((author) => author.equal)) {
    packages.push(`\\usepackage{${equal.latexPackage}}`);
  }
  const authorLines = block.authors.map((author) => {
    const marks = [
      ...author.marks,
      ...(author.equal ? [equal.latex] : []),
      ...(author.corresponding ? [CORRESPONDING_MARK] : []),
    ];
    const orcid = author.orcid ? ` \\orcidlink{${author.orcid}}` : "";
    const mark = marks.length > 0 ? `[${marks.join(",")}]` : "";
    return `\\author${mark}{${escapeLatex(author.name)}${orcid}}`;
  });
  const affilLines = block.affiliations.map(
    (affiliation) =>
      `\\affil[${affiliation.marker}]{${escapeLatex(affiliation.text)}}`
  );
  if (block.authors.some((author) => author.equal)) {
    affilLines.push(
      `\\affil[${equal.latex}]{${escapeLatex(templates.equalNote)}}`
    );
  }
  if (block.authors.some((author) => author.corresponding)) {
    const emails = block.authors
      .filter((author) => author.corresponding)
      .map(
        (author) =>
          author.email || t(templates.placeholderEmail, { name: author.name })
      )
      .join(templates.emailSeparator);
    affilLines.push(
      `\\affil[${CORRESPONDING_MARK}]{${escapeLatex(
        t(templates.correspondingNote, {
          label: templates.correspondingLabel,
          emails,
        })
      )}}`
    );
  }
  return [
    templates.preambleComment,
    ...packages,
    "",
    templates.bodyComment,
    ...authorLines,
    ...affilLines,
  ].join("\n");
}
